import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Trophy, Medal, Crown, TrendingUp, Zap, Target } from 'lucide-react';
import { cn } from '../../lib/utils';
interface LeaderboardScreenProps {
  currentUser?: {
    name: string;
    rank: number;
  };
}
// Mock leaderboard data
const GLOBAL_LEADERBOARD = [{
  rank: 1,
  name: 'Alex Champion',
  level: 45,
  wins: 892,
  winRate: 87,
  streak: 12,
  avatar: '🏆'
}, {
  rank: 2,
  name: 'Sarah Pro',
  level: 43,
  wins: 845,
  winRate: 85,
  streak: 8,
  avatar: '⭐'
}, {
  rank: 3,
  name: 'Mike Master',
  level: 41,
  wins: 798,
  winRate: 82,
  streak: 15,
  avatar: '🎯'
}, {
  rank: 4,
  name: 'Emma Swift',
  level: 39,
  wins: 756,
  winRate: 80,
  streak: 6,
  avatar: '⚡'
}, {
  rank: 5,
  name: 'Chris Ace',
  level: 38,
  wins: 723,
  winRate: 78,
  streak: 9,
  avatar: '🔥'
}, {
  rank: 6,
  name: 'Lisa Quick',
  level: 36,
  wins: 689,
  winRate: 76,
  streak: 4,
  avatar: '💫'
}, {
  rank: 7,
  name: 'Tom Flash',
  level: 35,
  wins: 654,
  winRate: 74,
  streak: 7,
  avatar: '🌟'
}, {
  rank: 8,
  name: 'Nina Speed',
  level: 34,
  wins: 621,
  winRate: 72,
  streak: 5,
  avatar: '✨'
}, {
  rank: 9,
  name: 'Ryan Fast',
  level: 33,
  wins: 589,
  winRate: 70,
  streak: 3,
  avatar: '💥'
}, {
  rank: 10,
  name: 'Kate Sharp',
  level: 32,
  wins: 556,
  winRate: 68,
  streak: 11,
  avatar: '🎪'
}, {
  rank: 12,
  name: 'You',
  level: 12,
  wins: 156,
  winRate: 65,
  streak: 3,
  avatar: '👤'
}];
const FRIENDS_LEADERBOARD = [{
  rank: 1,
  name: 'Sarah Pro',
  level: 43,
  wins: 845,
  winRate: 85,
  streak: 8,
  avatar: '⭐'
}, {
  rank: 2,
  name: 'Mike Master',
  level: 41,
  wins: 798,
  winRate: 82,
  streak: 15,
  avatar: '🎯'
}, {
  rank: 3,
  name: 'You',
  level: 12,
  wins: 156,
  winRate: 65,
  streak: 3,
  avatar: '👤'
}, {
  rank: 4,
  name: 'Emma Swift',
  level: 39,
  wins: 756,
  winRate: 80,
  streak: 6,
  avatar: '⚡'
}, {
  rank: 5,
  name: 'Chris Ace',
  level: 38,
  wins: 723,
  winRate: 78,
  streak: 9,
  avatar: '🔥'
}];
const WEEKLY_LEADERBOARD = [{
  rank: 1,
  name: 'Nina Speed',
  level: 34,
  wins: 45,
  winRate: 90,
  streak: 12,
  avatar: '✨'
}, {
  rank: 2,
  name: 'You',
  level: 12,
  wins: 38,
  winRate: 76,
  streak: 5,
  avatar: '👤'
}, {
  rank: 3,
  name: 'Ryan Fast',
  level: 33,
  wins: 35,
  winRate: 74,
  streak: 8,
  avatar: '💥'
}, {
  rank: 4,
  name: 'Sarah Pro',
  level: 43,
  wins: 32,
  winRate: 80,
  streak: 6,
  avatar: '⭐'
}, {
  rank: 5,
  name: 'Tom Flash',
  level: 35,
  wins: 28,
  winRate: 70,
  streak: 4,
  avatar: '🌟'
}];
type LeaderboardTab = 'global' | 'friends' | 'weekly';
export function LeaderboardScreen({
  currentUser
}: LeaderboardScreenProps) {
  const [activeTab, setActiveTab] = useState<LeaderboardTab>('global');
  const getLeaderboardData = () => {
    switch (activeTab) {
      case 'global':
        return GLOBAL_LEADERBOARD;
      case 'friends':
        return FRIENDS_LEADERBOARD;
      case 'weekly':
        return WEEKLY_LEADERBOARD;
      default:
        return GLOBAL_LEADERBOARD;
    }
  };
  const leaderboardData = getLeaderboardData();
  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="text-amber-500" size={24} data-id="element-356" />;
    if (rank === 2) return <Medal className="text-slate-400" size={24} data-id="element-357" />;
    if (rank === 3) return <Medal className="text-amber-700" size={24} data-id="element-358" />;
    return <span className="text-slate-500 font-bold text-lg" data-id="element-359">#{rank}</span>;
  };
  const getRankBadge = (rank: number) => {
    if (rank === 1) return <Badge variant="warning" className="bg-amber-500 text-white" data-id="element-360">
          Champion
        </Badge>;
    if (rank <= 3) return <Badge variant="warning" data-id="element-361">Top 3</Badge>;
    if (rank <= 10) return <Badge variant="info" data-id="element-362">Top 10</Badge>;
    if (rank <= 100) return <Badge variant="success" data-id="element-363">Top 100</Badge>;
    return null;
  };
  return <div className="flex flex-col h-full bg-slate-50" data-id="element-364">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-500 to-violet-600 text-white p-6 pb-8" data-id="element-365">
        <div className="flex items-center justify-center gap-3 mb-4" data-id="element-366">
          <Trophy size={32} data-id="element-367" />
          <h1 className="text-3xl font-black" data-id="element-368">Leaderboard</h1>
        </div>
        <p className="text-center text-blue-100" data-id="element-369">
          {activeTab === 'global' && 'Compete with the best players worldwide'}
          {activeTab === 'friends' && 'See how you rank among friends'}
          {activeTab === 'weekly' && "This week's top performers"}
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-4 -mt-4 rounded-t-3xl" data-id="element-370">
        <div className="flex gap-2 pt-4" data-id="element-371">
          {[{
          id: 'global' as LeaderboardTab,
          label: '🌍 Global'
        }, {
          id: 'friends' as LeaderboardTab,
          label: '👥 Friends'
        }, {
          id: 'weekly' as LeaderboardTab,
          label: '📅 Weekly'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('flex-1 py-3 px-4 rounded-t-xl font-semibold text-sm transition-colors', activeTab === tab.id ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700')} data-id="element-372">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-id="element-373">
        {leaderboardData.map((player, index) => {
        const isCurrentUser = player.name === 'You';
        return <Card key={index} className={cn('transition-all hover:shadow-md', isCurrentUser && 'ring-2 ring-blue-500 bg-blue-50')} data-id="element-374">
              <div className="flex items-center gap-4" data-id="element-375">
                {/* Rank */}
                <div className="flex-shrink-0 w-12 flex items-center justify-center" data-id="element-376">
                  {getRankIcon(player.rank)}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 flex items-center justify-center text-2xl" data-id="element-377">
                  {player.avatar}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" data-id="element-378">
                  <div className="flex items-center gap-2 mb-1" data-id="element-379">
                    <h3 className="font-bold text-slate-900 truncate" data-id="element-380">
                      {player.name}
                    </h3>
                    {getRankBadge(player.rank)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-381">
                    <span className="flex items-center gap-1" data-id="element-382">
                      <Trophy size={12} data-id="element-383" />
                      Lvl {player.level}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-384">
                      <Target size={12} data-id="element-385" />
                      {player.wins}W
                    </span>
                    <span className="flex items-center gap-1" data-id="element-386">
                      <TrendingUp size={12} data-id="element-387" />
                      {player.winRate}%
                    </span>
                  </div>
                </div>

                {/* Streak */}
                {player.streak > 0 && <div className="flex-shrink-0 flex flex-col items-center" data-id="element-388">
                    <div className="text-orange-500 text-xl" data-id="element-389">🔥</div>
                    <div className="text-xs font-bold text-slate-700" data-id="element-390">
                      {player.streak}
                    </div>
                  </div>}
              </div>
            </Card>;
      })}
      </div>

      {/* Current User Stats Footer */}
      {currentUser && <div className="bg-white border-t border-slate-200 p-4" data-id="element-391">
          <div className="flex items-center justify-between text-sm" data-id="element-392">
            <span className="text-slate-600" data-id="element-393">Your Rank</span>
            <span className="font-bold text-slate-900" data-id="element-394">
              #{currentUser.rank}
            </span>
          </div>
        </div>}
    </div>;
}